import { useState, useEffect } from 'react';
import { mlApi, type MLPrediction } from '../api/client';
import clsx from 'clsx';
import {
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

interface MLScoreDisplayProps {
  disputeId?: string;
  orderId?: string;
  showDetails?: boolean;
  compact?: boolean;
}

export default function MLScoreDisplay({
  disputeId,
  orderId,
  showDetails = false,
  compact = false,
}: MLScoreDisplayProps) {
  const [prediction, setPrediction] = useState<MLPrediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (disputeId || orderId) {
      fetchPrediction();
    }
  }, [disputeId, orderId]);

  async function fetchPrediction() {
    setLoading(true);
    try {
      if (disputeId) {
        const res = await mlApi.getDisputePrediction(disputeId);
        setPrediction(res.data.prediction);
      } else if (orderId) {
        const res = await mlApi.getOrderPrediction(orderId);
        setPrediction(res.data.prediction);
      }
    } catch (err) {
      console.error('Failed to fetch ML prediction:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className={clsx('flex items-center gap-2', compact ? 'text-xs' : 'text-sm')}>
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400">Analyzing...</span>
      </div>
    );
  }

  if (!prediction) {
    return null;
  }

  const isTraining = !prediction.is_trained;
  const samplesNeeded = prediction.min_samples_required - prediction.training_samples;
  const scorePercent = Math.round(prediction.win_probability * 100);

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-emerald-600 bg-emerald-50';
    if (score >= 50) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getActionBadge = (action: MLPrediction['recommended_action']) => {
    switch (action) {
      case 'auto_dispute':
        return { text: 'Auto-Dispute', color: 'bg-emerald-100 text-emerald-700' };
      case 'review':
        return { text: 'Review', color: 'bg-amber-100 text-amber-700' };
      case 'skip':
        return { text: 'Low Priority', color: 'bg-slate-100 text-slate-700' };
    }
  };

  const actionBadge = getActionBadge(prediction.recommended_action);

  // Compact mode for table cells
  if (compact) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className="flex items-center gap-1.5">
          {isTraining ? (
            <>
              <BeakerIcon className="w-4 h-4 text-amber-500" />
              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600">
                Training
              </span>
            </>
          ) : (
            <>
              <SparklesIcon className="w-4 h-4 text-indigo-500" />
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-xs font-medium',
                  getScoreColor(scorePercent)
                )}
              >
                {scorePercent}%
              </span>
            </>
          )}
        </div>

        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute z-50 bottom-full left-0 mb-2 w-72 p-3 bg-white rounded-lg shadow-xl border border-slate-200">
            {isTraining ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <BeakerIcon className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-slate-900">ML Model Training</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                  <p className="text-xs text-amber-800">
                    <strong>{samplesNeeded} more dispute outcomes needed</strong> before ML predictions become accurate.
                  </p>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Training Progress</span>
                    <span className="font-medium">{prediction.training_samples} / {prediction.min_samples_required}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{ width: `${(prediction.training_samples / prediction.min_samples_required) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Record dispute outcomes (won/lost) to train the model.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <SparklesIcon className="w-4 h-4 text-indigo-500" />
                  <span className="font-medium text-slate-900">ML Win Prediction</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Win Probability</span>
                    <span className="font-medium">{scorePercent}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Confidence</span>
                    <span className="font-medium">{Math.round(prediction.confidence * 100)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Recommendation</span>
                    <span className={clsx('px-1.5 py-0.5 rounded text-xs', actionBadge.color)}>
                      {actionBadge.text}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Training Data</span>
                    <span className="font-medium">{prediction.training_samples} outcomes</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Training mode - full display
  if (isTraining) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BeakerIcon className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-slate-900">ML Model Training</span>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            {prediction.training_samples} / {prediction.min_samples_required} samples
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-amber-700">Training Progress</span>
            <span className="font-medium text-amber-800">
              {Math.round((prediction.training_samples / prediction.min_samples_required) * 100)}%
            </span>
          </div>
          <div className="w-full h-3 bg-amber-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all"
              style={{ width: `${(prediction.training_samples / prediction.min_samples_required) * 100}%` }}
            />
          </div>
        </div>

        {/* Explanation */}
        <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                {samplesNeeded} more dispute outcomes needed
              </p>
              <p className="text-xs text-amber-700 mt-1">
                The ML model learns from your dispute history. Record outcomes (won/lost/partial)
                on {samplesNeeded} more disputes to unlock accurate win predictions.
              </p>
            </div>
          </div>
        </div>

        {/* Preview Score (with disclaimer) */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            <p className="text-xs text-amber-600 mb-2 font-medium">Preview (using default weights)</p>
            <div className="flex items-center gap-3">
              <div className="relative w-12 h-12">
                <svg className="w-full h-full transform -rotate-90 opacity-50">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#fcd34d" strokeWidth="4" />
                  <circle
                    cx="24" cy="24" r="20" fill="none"
                    stroke="#f59e0b"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${(scorePercent / 100) * 126} 126`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-amber-700">{scorePercent}</span>
                </div>
              </div>
              <div className="text-xs text-amber-700">
                <p>Estimated win probability</p>
                <p className="text-amber-500">(accuracy improves with training)</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Trained model - full display
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-indigo-600" />
          <span className="font-semibold text-slate-900">ML Win Prediction</span>
        </div>
        <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', actionBadge.color)}>
          {actionBadge.text}
        </span>
      </div>

      {/* Score Display */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-20 h-20">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="40"
              cy="40"
              r="36"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="8"
            />
            <circle
              cx="40"
              cy="40"
              r="36"
              fill="none"
              stroke={scorePercent >= 75 ? '#10b981' : scorePercent >= 50 ? '#f59e0b' : '#ef4444'}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${(scorePercent / 100) * 226} 226`}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-slate-900">{scorePercent}</span>
          </div>
        </div>

        <div className="flex-1">
          <div className="text-sm text-slate-600 mb-1">
            Confidence: {Math.round(prediction.confidence * 100)}%
          </div>
          <div className="text-xs text-slate-500">
            Trained on {prediction.training_samples} dispute outcomes
          </div>
        </div>
      </div>

      {/* Contributing Factors */}
      {showDetails && prediction.contributing_factors.length > 0 && (
        <div className="border-t border-indigo-100 pt-3">
          <div className="flex items-center gap-1 mb-2">
            <InformationCircleIcon className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-700">Key Factors</span>
          </div>
          <div className="space-y-1.5">
            {prediction.contributing_factors.slice(0, 5).map((factor, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5">
                  {factor.impact === 'positive' ? (
                    <ArrowTrendingUpIcon className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-slate-600">{factor.feature}</span>
                </div>
                <span
                  className={clsx(
                    'text-xs font-medium',
                    factor.impact === 'positive' ? 'text-emerald-600' : 'text-red-600'
                  )}
                >
                  {factor.impact === 'positive' ? '+' : '-'}
                  {Math.round(factor.weight * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
